import { inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { HttpInterceptorFn } from '@angular/common/http';

/**
 * utilities
 */
import { AppHttpUtils } from '../../../utilities';

/**
 * services
 */
import { LOADING_HTTP_CONTROL } from '@core/services/loading';

/**
 * Loading HTTP Interceptor
 * 
 * @function loadingInterceptor
 * @description Automatically tracks HTTP requests and manages global loading state.
 * Checks if request has show loading context to determine if loading should be shown.
 * Removes the X-Show-Loading header before sending request to server.
 * 
 * @param {HttpRequest<any>} req - The outgoing HTTP request
 * @param {HttpHandlerFn} next - The next interceptor in the chain
 * @returns {Observable<HttpEvent<any>>} The HTTP response with loading state management
 * 
 * @example
 * ```typescript
 * // Automatic loading (default behavior)
 * this.http.get('/api/users').subscribe();
 * 
 * // Show loading for specific request using show loading context
 * this.http.get('/api/users', { 
 *   context: new HttpContext().set(LOADING_SHOW_CONTEXT, true) 
 * }).subscribe();
 * 
 * // Disable loading for specific request using show loading context
 * this.http.get('/api/users', { 
 *   context: new HttpContext().set(LOADING_SHOW_CONTEXT, false)
 * }).subscribe();
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {


  // Check if request has show loading context
  const shouldShowLoading = AppHttpUtils.isShowLoading(req);

  // If request does not have show loading context, skip loading
  if (!shouldShowLoading) {
    return next(req);
  }

  // Inject loading service using HTTP control token
  const _loadingService = inject(LOADING_HTTP_CONTROL);
  
  // Generate unique request ID
  const requestId = _loadingService.generateRequestId(req.url, req.method);
  
  // Add request to active requests
  _loadingService.addRequest(requestId);
  
  // Process the request and ensure loading state is updated when complete
  return next(req).pipe(
    finalize(() => {

      // Remove request from active requests
      _loadingService.removeRequest(requestId);

    })
  );

};
